/**
 * Optional Gemini integration.
 *
 * WHAT THIS DOES: turns her words (and optionally a photo) into a structured
 * DesignBrief — colours, mood, a style leaning, maybe a word to spell.
 *
 * WHAT THIS DOES NOT DO: produce knots, rows, charts, thread lengths, or any
 * other geometry. A language model cannot simulate a string permutation
 * reliably, and a bracelet built on a hallucinated chart wastes an evening and
 * a pile of floss. Everything downstream of the brief is deterministic code
 * with golden tests. If this module returns nonsense, the worst case is an
 * ugly colour scheme, not a bracelet that cannot be made.
 *
 * The key is the user's own, lives in localStorage, and is sent directly from
 * her browser to Google. Knotwright has no backend and never sees it.
 */
import { nearestFloss, flossByCode, FLOSS } from '../data/dmc';
import { interpretHeuristically } from './heuristic';
import type { DesignBrief } from './types';
import type { BraceletStyle, MotifKind, Sizing } from '../engine/types';

export const DEFAULT_GEMINI_MODEL = 'gemini-2.0-flash';

const ENDPOINT = (model: string) =>
  `https://generativelanguage.googleapis.com/v1beta/models/${encodeURIComponent(model)}:generateContent`;

export interface GeminiRequest {
  apiKey: string;
  text: string;
  sizing: Sizing;
  /** Optional photo, as a data URL. */
  imageDataUrl?: string;
  model?: string;
  signal?: AbortSignal;
}

export class GeminiError extends Error {
  constructor(
    message: string,
    readonly kind: 'no-key' | 'auth' | 'quota' | 'network' | 'bad-response',
  ) {
    super(message);
    this.name = 'GeminiError';
  }
}

/** True only for a key that is actually present. Whitespace is not a key. */
export function hasGeminiKey(key: string | null | undefined): boolean {
  return typeof key === 'string' && key.trim().length > 0;
}

/**
 * The schema the model is pinned to. Note that every field is about INTENT.
 * There is deliberately no field in which a knot could be expressed.
 */
const RESPONSE_SCHEMA = {
  type: 'OBJECT',
  properties: {
    mood: {
      type: 'STRING',
      description: 'Two to five words describing the feel, e.g. "warm summer sunset".',
    },
    colors: {
      type: 'ARRAY',
      description: 'Three to five hex colours, in the order they should appear, lead colour first.',
      items: { type: 'STRING', description: 'A hex colour like #ff8844' },
    },
    style: {
      type: 'STRING',
      description:
        'One of: normal (chevrons, stripes, diamonds), alpha (pixel pictures and words), chain (daisy or fruit motif chains), any.',
      enum: ['normal', 'alpha', 'chain', 'any'],
    },
    word: {
      type: 'STRING',
      description:
        'A short word to spell out, ONLY if the request clearly asks for text. Otherwise an empty string.',
    },
    motifs: {
      type: 'ARRAY',
      description: 'Motifs explicitly implied by the request, if any.',
      items: { type: 'STRING', enum: ['daisy', 'cherry', 'strawberry', 'heart'] },
    },
  },
  required: ['mood', 'colors', 'style'],
} as const;

const SYSTEM_PROMPT = `You are a colour and mood interpreter for a friendship-bracelet design tool.

Given a short description (and possibly a photo), return a design brief: a mood
phrase, an ordered list of 3-5 hex colours, a style leaning, an optional word to
spell, and any motifs that were clearly asked for.

Rules:
- Choose colours that are actually distinguishable from each other when sitting
  side by side as threads. Avoid two near-identical shades.
- Order the colours the way they should read across the bracelet.
- Only set "word" if the request clearly asks for text on the bracelet. A
  bracelet described as "for Maya" is not asking for the word MAYA unless it
  says so. Words must be A-Z, 0-9 and spaces only, and at most 8 characters.
- Only set "style" to something other than "any" if the request implies it.
- Never describe knots, rows, charts or thread lengths. You are not being asked
  how to make the bracelet, only what it should look like.`;

interface RawBrief {
  mood?: unknown;
  colors?: unknown;
  style?: unknown;
  word?: unknown;
  motifs?: unknown;
}

/**
 * Interpret a request with Gemini, then SNAP the result onto the real floss
 * palette. The model suggests colours in the abstract; the palette decides what
 * can actually be bought, so its answer is always mapped to DMC codes here.
 */
export async function interpretWithGemini(req: GeminiRequest): Promise<DesignBrief> {
  if (!hasGeminiKey(req.apiKey)) {
    throw new GeminiError('No Gemini API key is set, so no request was made.', 'no-key');
  }

  const parts: unknown[] = [{ text: `${SYSTEM_PROMPT}\n\nRequest: ${req.text || '(no words given)'}` }];
  if (req.imageDataUrl) {
    const inline = dataUrlToInline(req.imageDataUrl);
    if (inline) parts.push({ inline_data: inline });
  }

  let response: Response;
  try {
    response = await fetch(`${ENDPOINT(req.model ?? DEFAULT_GEMINI_MODEL)}`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        'x-goog-api-key': req.apiKey.trim(),
      },
      signal: req.signal,
      body: JSON.stringify({
        contents: [{ role: 'user', parts }],
        generationConfig: {
          temperature: 0.7,
          responseMimeType: 'application/json',
          responseSchema: RESPONSE_SCHEMA,
        },
      }),
    });
  } catch (err) {
    throw new GeminiError(
      `Could not reach Google: ${err instanceof Error ? err.message : String(err)}`,
      'network',
    );
  }

  if (response.status === 400 || response.status === 401 || response.status === 403) {
    throw new GeminiError(
      'Google rejected that API key. Check it was pasted in full, and that the Generative Language API is enabled for it.',
      'auth',
    );
  }
  if (response.status === 429) {
    throw new GeminiError(
      'That key has hit its free-tier rate limit. Wait a minute and try again, or carry on without AI.',
      'quota',
    );
  }
  if (!response.ok) {
    throw new GeminiError(`Google returned HTTP ${response.status}.`, 'network');
  }

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    throw new GeminiError('Google returned something that was not JSON.', 'bad-response');
  }

  const raw = extractJson(payload);
  if (!raw) {
    throw new GeminiError('Google returned no usable design brief.', 'bad-response');
  }

  return briefFromRaw(raw, req);
}

/* -------------------------------------------------------------------------- */

function extractJson(payload: unknown): RawBrief | null {
  const text = (payload as any)?.candidates?.[0]?.content?.parts?.[0]?.text;
  if (typeof text !== 'string') return null;
  try {
    const parsed = JSON.parse(text);
    return parsed && typeof parsed === 'object' ? (parsed as RawBrief) : null;
  } catch {
    return null;
  }
}

/**
 * Convert the model's answer into a brief. Everything is validated: a bad or
 * missing field falls back to the heuristic interpreter rather than producing
 * a broken brief, because a broken brief becomes a broken bracelet.
 */
export function briefFromRaw(raw: RawBrief, req: GeminiRequest): DesignBrief {
  const fallback = interpretHeuristically(req.text, { sizing: req.sizing });

  const codes = toFlossCodes(raw.colors);
  const colors = codes.length >= 2 ? codes : fallback.colors;

  const style = isStyle(raw.style) ? raw.style : 'any';
  const word = cleanWord(raw.word);
  const motifs = toMotifs(raw.motifs);
  const mood = typeof raw.mood === 'string' && raw.mood.trim() ? raw.mood.trim() : fallback.mood;

  const notes: string[] = [];
  if (codes.length < 2) notes.push('its colour list was unusable, so the built-in palette was used');
  const usedImage = Boolean(req.imageDataUrl);

  return {
    text: req.text,
    colors,
    stylePreference: word ? 'alpha' : style,
    word,
    motifs,
    mood,
    sizing: req.sizing,
    source: 'gemini',
    sourceNote:
      `Colours and mood interpreted by Google Gemini${usedImage ? ' from your words and photo' : ' from your words'}, ` +
      `then matched to the nearest real DMC floss. All knots, counts and lengths were worked out by Knotwright, not by the AI.` +
      (notes.length ? ` (Note: ${notes.join('; ')}.)` : ''),
  };
}

function toFlossCodes(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const out: string[] = [];
  for (const item of value) {
    if (typeof item !== 'string') continue;
    const hex = item.trim();
    // Accept a DMC code directly, in case the model volunteers one.
    const direct = flossByCode(hex);
    const floss = direct ?? (/^#?[0-9a-f]{3}([0-9a-f]{3})?$/i.test(hex) ? nearestFloss(hex.startsWith('#') ? hex : `#${hex}`) : undefined);
    if (floss && !out.includes(floss.dmc)) out.push(floss.dmc);
    if (out.length >= 5) break;
  }
  return out;
}

function toMotifs(value: unknown): MotifKind[] {
  const valid: MotifKind[] = ['daisy', 'cherry', 'strawberry', 'heart'];
  if (!Array.isArray(value)) return [];
  return [...new Set(value.filter((m): m is MotifKind => valid.includes(m as MotifKind)))];
}

function isStyle(value: unknown): value is BraceletStyle | 'any' {
  return value === 'normal' || value === 'alpha' || value === 'chain' || value === 'any';
}

function cleanWord(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const w = value.toUpperCase().replace(/[^A-Z0-9 ]/g, '').trim().slice(0, 8);
  return w.length > 0 ? w : undefined;
}

function dataUrlToInline(dataUrl: string): { mime_type: string; data: string } | null {
  const m = /^data:([^;]+);base64,(.*)$/.exec(dataUrl);
  if (!m) return null;
  return { mime_type: m[1]!, data: m[2]! };
}

/** Every floss code the model is allowed to land on, for the settings screen. */
export const AVAILABLE_FLOSS_COUNT = FLOSS.length;
